import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../hooks/useApi";
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Card from "../components/Card";
import Button from "../components/Buttons";
import LoadingSpinner from "../components/LoadingSpinner";
import { Plus, FileText, Shield, Trash2, RefreshCw, Upload, Users, Eye } from "lucide-react";
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import OrgAssignAssessModal from "../components/OrgAssignAssessModal";
import TPRMRespondentsModal from "../components/TPRMRespondentsModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import TPRMVendorSelectModal from "../components/TPRMVendorSelectModal";
import { useRBAC } from "../hooks/useRBAC";
import { useURLState } from "../hooks/useURLState";
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';
import AssessmentActivityLog from "../components/AssessmentActivityLog";

import { SHOW_DELETE_ASSESSMENT } from '../config';
const TPRMOrganizations = () => {
  const [assessments, setAssessments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [creatingAssessment, setCreatingAssessment] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);

  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const [pageSize, setPageSize] = usePageSize('tprmOrganizations');
  const listTopRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user } = useAuth();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canViewOrganizationAssessments, canAssessViaAssignment, canDeleteAssessments } = useRBAC();
  const canDeleteAssessment = SHOW_DELETE_ASSESSMENT && canDeleteAssessments(); // super admin only, and the switch in config.js
  const selectedOrgData = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();

  /* ================================
     Fetch Org-Level Assessments (with pre-aggregated risk data)
     ================================ */
  const fetchAssessments = async () => {
    try {
      const data = await apiCall("GET", "/TPRMAssessments/summary");

      const normalized = (Array.isArray(data) ? data : []).map((a) => ({
        ...a,
        hasRisks: a.totalRisks > 0,
        riskCounts: {
          CRITICAL: a.riskCounts?.critical || 0,
          HIGH: a.riskCounts?.high || 0,
          MEDIUM: a.riskCounts?.medium || 0,
          LOW: a.riskCounts?.low || 0,
        },
      }));

      setAssessments(normalized);
    } catch (err) {} finally {
      setLoading(false);
    }
  };

  /* ======================================
     Handle Vendor Created + Auto-Create Assessment
     ====================================== */
  const handleVendorCreatedWithAssessment = async (newVendor) => {
    setCreatingAssessment(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));

      // Create org-level TPRM Assessment (DealId = null)
      const assessmentPayload = {
        DealId: null,
        OrganizationId: org.organizationId,
        VendorId: newVendor.vendorId,
        RegionId: newVendor.regionId || null,
        VerticalId: newVendor.verticalId || null,
        TPRMAssessmentType: "Initial",
        TPRMFramework: "Default",
        TPRMAssessmentDate: new Date().toISOString(),
        TPRMStatus: "in_progress",
        TPRMProgress: 0,
      };

      await apiCall("POST", "/TPRMAssessments", assessmentPayload);

      setShowNewVendorModal(false);
      fetchAssessments(); // Refresh the list
    } catch (err) {
      alert("Vendor created, but assessment creation failed. You can assign manually.");
      setShowNewVendorModal(false);
      fetchAssessments();
    } finally {
      setCreatingAssessment(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const data = await apiCall("GET", "/TPRMVendor/vendors?pageSize=100");
      setVendors(data.items || []);
    } catch (err) {
      setVendors([]);
    }
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/tprmAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchAssessments();
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
        await apiCall('POST', `/tprmAssessments/${assessmentToReassess}/reassess`);
        setShowReassessModal(false);
        setAssessmentToReassess(null);
        navigate(`/TPRMAssessment/${assessmentToReassess}?step=review`);
    } catch (error) {} finally {
        setReassessing(false);
    }
  };

  /* ================================
     Effects
     ================================ */
  useEffect(() => {
    fetchAssessments();
    fetchVendors();
  }, []);

  useEffect(() => {
    const autoSelectAssessmentId = location.state?.assessmentId;
    if (autoSelectAssessmentId && assessments.length) {
      const found = assessments.find(
        (a) => a.tprmAssessmentID === autoSelectAssessmentId
      );
      if (found) setSelectedAssessment(found);
    }
  }, [assessments, location.state]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">
            TPRM Enterprise Assessments
          </h1>
          <p className="mt-2 text-gray-600">
            Manage organization-level TPRM vendor assessments
          </p>
        </div>

        <Card>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Vendor Assessments</h2>
            {canManageRisks && (
              <Button
                size="sm"
                onClick={() => setShowNewVendorModal(true)}
                disabled={creatingAssessment}
              >
                <Plus className="h-4 w-4 mr-2" />
                {creatingAssessment ? "Creating..." : "Add Vendor"}
              </Button>
            )}
          </div>

          {showNewVendorModal && (
            <TPRMVendorSelectModal
              isOpen={showNewVendorModal}
              onClose={() => setShowNewVendorModal(false)}
              onVendorSelected={handleVendorCreatedWithAssessment}
              vendors={vendors}
              existingVendorIds={assessments.map(a => a.vendorId)}
              onVendorCreated={(newVendor) => setVendors(prev => [...prev, newVendor])}
            />
          )}

          <div className="space-y-3" ref={listTopRef}>
            {assessments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
              </div>
            ) : (
              assessments.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((assessment) => (
                <div
                  key={assessment.tprmAssessmentID}
                  className="p-4 border-2 rounded-lg transition-colors border-gray-200 hover:border-gray-300"
                >
                  <div className="flex gap-4 items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="font-medium text-content-primary">
                            {assessment.vendorName || `Vendor ID: ${assessment.vendorId}`}
                            <AssessmentStatusBadge status={assessment.tprmStatus} />
                          </h3>
                          <p className="text-sm text-gray-500">
                            {assessment.vendorDomain || ""}
                          </p>
                          {assessment.tprmAssignedToUserName && (
                            <p className="text-xs text-blue-600 mt-1">
                              Assigned to: <span className="font-semibold">{assessment.tprmAssignedToUserName}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Risk details */}
                      {assessment.hasRisks ? (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-2">
                            {assessment.riskCounts?.CRITICAL > 0 && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{assessment.riskCounts.CRITICAL} Critical</span>}
                            {assessment.riskCounts?.HIGH > 0 && <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{assessment.riskCounts.HIGH} High</span>}
                            {assessment.riskCounts?.MEDIUM > 0 && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{assessment.riskCounts.MEDIUM} Medium</span>}
                            {assessment.riskCounts?.LOW > 0 && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{assessment.riskCounts.LOW} Low</span>}
                          </div>
                          <div className="mt-2">
                            <div className="text-xs text-gray-500 mb-1">Risk Score: {assessment.riskScore || 0}%</div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div className={`h-3 rounded-full ${assessment.riskScore <= 40 ? "bg-green-500" : assessment.riskScore <= 60 ? "bg-yellow-500" : assessment.riskScore <= 80 ? "bg-orange-500" : "bg-red-500"}`} style={{ width: `${assessment.riskScore || 0}%` }} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 text-xs text-gray-500">No Risk Data</div>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                    {canManageRisks && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={assessment.tprmStatus === "completed"}
                        title={assessment.tprmStatus === "completed" ? "Assessment is completed" : ""}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAssessment(assessment);
                          setShowAssignModal(true);
                        }}
                      >
                        {assessment.tprmAssignedToUserId ? "Reassign" : "Assign"}
                      </Button>
                    )}

                    {canManageRisks && assessment.tprmAssessmentID && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="whitespace-nowrap"
                        title="Manage additional respondents for this assessment"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRespondentsAssessmentId(assessment.tprmAssessmentID);
                        }}
                      >
                        <Users className="h-4 w-4 mr-1" />
                        Respondents
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={
                        !canAssess(assessment.tprmAssignedToUserId, undefined, undefined, undefined, assessment.assigneeUserIds || []) ||
                        assessment.tprmStatus === "completed" ||
                        assessment.tprmStatus === "analyzing"
                      }
                      title={
                        assessment.tprmStatus === "completed" ? "Assessment is completed"
                          : assessment.tprmStatus === "analyzing" ? "Assessment is being analyzed"
                          : getAssessDisabledReason(assessment.tprmAssignedToUserId, undefined, undefined, undefined, assessment.assigneeUserIds || [])
                            || ""
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!assessment.tprmAssignedToUserId && !(assessment.assigneeUserIds || []).length) {
                          setShowUnassignedAlert(true);
                          return;
                        }
                        // Get vendorId from assessment, if 0 then from vendor object
                        const vendor = vendors.find(v => v.vendorId === assessment.vendorId);
                        const vendorId = assessment.vendorId || vendor?.vendorId || 0;

                        if (!vendorId || vendorId === 0) {
                          alert("No vendor associated. Please create a new assessment.");
                          return;
                        }

                        const tprmPath = assessment.tprmAssessmentID
                          ? `/TPRMAssessment/${assessment.tprmAssessmentID}`
                          : "/TPRMAssessment";
                        navigate(tprmPath, {
                          state: {
                            vendorId: vendorId,
                            vendorName: assessment.vendorName || vendor?.vendorName,
                            assessmentId: assessment.tprmAssessmentID,
                            organizationId: selectedOrgData?.organizationId,
                            verticalId: vendor?.verticalId || selectedOrgData?.verticalId,
                            regionId: vendor?.regionId || selectedOrgData?.regionId,
                            subVerticalId: vendor?.subVerticalId || selectedOrgData?.subVerticalId,
                          },
                        });
                      }}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Assess
                    </Button>

                    {/* Viewing mirrors the backend's GetViewableAssessmentAsync: org read or assignment. */}
                    {assessment.tprmAssessmentID && assessment.tprmStatus === "completed" &&
                      (canViewOrganizationAssessments() || canAssessViaAssignment(assessment.tprmAssignedToUserId, assessment.assigneeUserIds || [])) && (
                      <Button
                        size="sm"
                        variant="secondary"
                        title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/TPRMAssessment/${assessment.tprmAssessmentID}?step=review`);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Answers
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="secondary"
                      className="whitespace-nowrap"
                      title="Upload a vendor document for AI risk analysis"
                      onClick={(e) => {
                        e.stopPropagation();
                        const vendor = vendors.find(v => v.vendorId === assessment.vendorId);
                        const vendorId = assessment.vendorId || vendor?.vendorId || 0;
                        navigate('/TPRMDocumentAssessment', {
                          state: {
                            vendorId,
                            vendorName: assessment.vendorName || vendor?.vendorName,
                          }
                        });
                      }}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Assess Document
                    </Button>

                    {assessment.hasRisks ? (
                      <Button
                        size="sm"
                        className="whitespace-nowrap"
                        disabled={assessment.totalRisks === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/VendorDashboard/${assessment.vendorId}`);
                        }}
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        View Risks ({assessment.activeRisks ?? assessment.totalRisks})
                      </Button>
                    ) : (
                      <Button size="sm" variant="primary" disabled className="whitespace-nowrap">
                        <Shield className="h-4 w-4 mr-1" />
                        View Risks (0)
                      </Button>
                    )}
                    {canManageOrganizationRisks() && assessment.tprmAssessmentID && (
                      <Button size="sm" variant="warning" className="whitespace-nowrap"
                        disabled={assessment.tprmStatus !== 'completed'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssessmentToReassess(assessment.tprmAssessmentID);
                          setShowReassessModal(true);
                        }}>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Reassess
                      </Button>
                    )}
                    {canDeleteAssessment && assessment.tprmAssessmentID && (
                      <Button size="sm" variant="danger" className="whitespace-nowrap"
                        onClick={(e) => { e.stopPropagation(); setAssessmentToDelete(assessment.tprmAssessmentID); setShowDeleteModal(true); }}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    )}
                      </div>
                    </div>
                    <div className="w-px bg-gray-200 self-stretch flex-shrink-0 mx-1"></div>
                    <div className="w-80 flex-shrink-0 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Activity</p>
                      <AssessmentActivityLog assessmentType="tprm" assessmentId={String(assessment.tprmAssessmentID)} hideQuestionEvents />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination Controls */}
          <Pagination
            page={currentPage}
            totalPages={Math.max(1, Math.ceil(assessments.length / pageSize))}
            totalItems={assessments.length}
            pageSize={pageSize}
            onPageChange={setCurrentPageParam}
            onPageSizeChange={setPageSize}
            scrollTargetRef={listTopRef}
          />
        </Card>

        {showAssignModal && selectedAssessment && (
          <OrgAssignAssessModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            organizationId={selectedAssessment.organizationId}
            vendorId={selectedAssessment.vendorId}
            assessmentId={selectedAssessment.tprmAssessmentID}
            assessmentType="tprm"
            hasExistingAssessment={true}
            refreshData={() => fetchAssessments()}
          />
        )}

        <TPRMRespondentsModal
          isOpen={respondentsAssessmentId != null}
          onClose={() => setRespondentsAssessmentId(null)}
          assessmentId={respondentsAssessmentId}
          refreshData={() => fetchAssessments()}
        />

        <DeleteAssessmentModal isOpen={showDeleteModal}
          onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
          onConfirm={handleDeleteAssessment} isDeleting={deleting} />

        <ReassessModal
          isOpen={showReassessModal}
          onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
          onConfirm={handleReassessAssessment}
          isReassessing={reassessing}
        />

        <AssessmentUnassignedModal
          isOpen={showUnassignedAlert}
          onClose={() => setShowUnassignedAlert(false)}
        />
      </div>
    </div>
  );
};

export default TPRMOrganizations;
